import { Brain, Cpu, GitBranch, Layers, Link2, Network } from 'lucide-react'
import BrandLogo from '../../components/BrandLogo'

/* ═══════════════════════════════════════════════════════════════════════════
   Shared slides: cross-referencing data, API rails, relational intelligence.
   Used by MultiIndustryDeckPage and CrossReferenceDataDeckPage.
   ═══════════════════════════════════════════════════════════════════════════ */

export function SlideTag({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest ${className}`}>
      {children}
    </span>
  )
}

function TwoCol({
  tag,
  headline,
  headlineNode,
  highlight,
  subline,
  children,
  right,
}: {
  tag?: string
  headline?: string
  headlineNode?: React.ReactNode
  highlight?: string
  subline?: string
  children?: React.ReactNode
  right: React.ReactNode
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0">
      <div className="min-w-0">
        {tag && <SlideTag>{tag}</SlideTag>}
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] break-words">
          {headlineNode ?? (
            <>
              {headline ?? ''}
              {highlight && <span className="text-[#FF2300]">{highlight}</span>}
            </>
          )}
        </h2>
        {subline && <p className="mt-4 text-base sm:text-lg text-zinc-400 break-words">{subline}</p>}
        <div className="min-w-0">{children}</div>
      </div>
      <div className="flex items-center justify-center min-h-[280px] lg:min-h-[340px] min-w-0">
        {right}
      </div>
    </div>
  )
}

function GrayBox({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-zinc-800/40 p-5 min-w-0">
      <h3 className="text-[15px] font-semibold text-white mb-2">{title}</h3>
      <p className="text-[14px] sm:text-base text-zinc-400 leading-relaxed break-words">{children}</p>
    </div>
  )
}

/** Title slide for standalone /deck-cross-reference-data only */
export function SlideCrossReferenceDeckTitle() {
  return (
    <div>
      <div className="flex items-center gap-5 mb-8">
        <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-[#FF2300] rounded-sm flex-shrink-0" aria-hidden />
        <BrandLogo className="h-10 sm:h-12 lg:h-14 w-auto text-white" />
      </div>
      <SlideTag className="mb-4">The data play</SlideTag>
      <p className="mt-2 text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-white leading-snug max-w-3xl">
        Value isn&apos;t only <span className="text-zinc-500">“all in one.”</span> It&apos;s{' '}
        <span className="text-[#FF2300]">cross-referenced data</span>
        {' — '}when ticketing, streaming, deals, and calendar reality live together, insight compounds.
      </p>
      <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed">
        DIZEE is built to be the system of record and the AI harness on top: an API optimized for agents,
        modular pieces that can extend to other industries or stand alone — and a unified graph that no
        single-point tool can replicate.
      </p>
      <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-2 text-base sm:text-[17px] text-white">
        <span className="inline-flex items-center rounded-full border border-[#FF2300]/30 bg-[#FF2300]/10 px-4 py-2 text-[14px] sm:text-[15px] font-medium text-[#FF2300]">
          Strategy
        </span>
        <span>Spring 2026</span>
        <span>dizee.com</span>
      </div>
    </div>
  )
}

/** API rails, AI harness, modular expansion */
export function SlidePlatformRails() {
  return (
    <TwoCol
      tag="Platform"
      headline="Rails for agents, products, and "
      highlight="new verticals"
      subline="The same core can power other agents and products built on music through an API designed for AI — not as a bolt-on, but as the harness models and tools plug into."
      right={
        <div className="relative w-full max-w-md flex flex-col items-center gap-6">
          <div className="flex h-36 w-36 items-center justify-center rounded-2xl border border-[#FF2300]/30 bg-[#FF2300]/[0.08]">
            <Cpu className="h-16 w-16 text-[#FF2300]" aria-hidden />
          </div>
          <div className="flex flex-wrap justify-center gap-2 text-[12px] text-zinc-500">
            <span className="rounded-full border border-white/[0.08] px-3 py-1">Agents</span>
            <span className="rounded-full border border-white/[0.08] px-3 py-1">Integrations</span>
            <span className="rounded-full border border-white/[0.08] px-3 py-1">Industry modules</span>
          </div>
        </div>
      }
    >
      <div className="mt-8 grid sm:grid-cols-2 gap-4 min-w-0">
        <GrayBox title="AI-native API">
          Built so intelligence layers can use career context safely and deeply — not just move JSON
          between silos.
        </GrayBox>
        <GrayBox title="Composable product">
          Components can extend DIZEE into other industries or ship as focused products with their own
          perceived value.
        </GrayBox>
        <GrayBox title="Consolidation that competes">
          Bring what teams already use into one system that has everything else they need — easier for
          users, stronger unit economics when the platform earns the relationship.
        </GrayBox>
        <GrayBox title="You become the source">
          When operational data lives in DIZEE, it&apos;s the system of record — not a shadow copy
          trapped in a single vendor&apos;s dashboard.
        </GrayBox>
      </div>
    </TwoCol>
  )
}

/** Cross-referencing beats siloed exports */
export function SlideCrossReferenceCore() {
  return (
    <TwoCol
      tag="Cross-reference"
      headline="Combined data beats "
      highlight="the same data alone"
      subline="Ticketing next to streaming, next to shows, money, and team context is more powerful than pulling tickets only from the ticketing platform. The signal is in the relationships."
      right={
        <div className="relative w-full max-w-lg aspect-[4/3] rounded-2xl border border-white/[0.08] bg-zinc-900/50 p-6 flex flex-col justify-center">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <span className="rounded-lg border border-[#FF2300]/40 bg-[#FF2300]/10 px-4 py-2 text-sm font-medium text-[#FF2300]">
              Tickets
            </span>
            <Link2 className="h-5 w-5 text-zinc-500 shrink-0" aria-hidden />
            <span className="rounded-lg border border-white/[0.12] bg-white/[0.04] px-4 py-2 text-sm text-zinc-300">
              Streaming
            </span>
            <Link2 className="h-5 w-5 text-zinc-500 shrink-0" aria-hidden />
            <span className="rounded-lg border border-white/[0.12] bg-white/[0.04] px-4 py-2 text-sm text-zinc-300">
              Shows / deals
            </span>
          </div>
          <div className="mt-8 flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-3 text-sm font-medium text-emerald-400">
              <Network className="h-4 w-4" aria-hidden />
              Richer insight than any single feed
            </div>
          </div>
        </div>
      }
    >
      <div className="mt-8 grid sm:grid-cols-1 gap-4 min-w-0">
        <GrayBox title="Not just convenience">
          “All in one” is table stakes. The unlock is{' '}
          <span className="text-zinc-200">cross-referencing</span>: the same fields mean more when they
          connect to the rest of the career graph.
        </GrayBox>
        <GrayBox title="Why silos cap intelligence">
          A great export from one tool still misses how it relates to revenue, calendar, partners, and
          narrative — the parts DIZEE is designed to hold together.
        </GrayBox>
      </div>
    </TwoCol>
  )
}

/** Relational intelligence + closing thesis */
export function SlideRelationalIntelligence() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start min-w-0">
      <div className="min-w-0">
        <SlideTag>Intelligence</SlideTag>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] break-words">
          Decisions from{' '}
          <span className="text-[#FF2300]">interconnected</span> signal — not random metrics
        </h2>
        <p className="mt-4 text-base sm:text-lg text-zinc-400 break-words">
          Intelligence compounds when the model (human or AI) can relate what matters: shows, money,
          releases, deals, and team context. That relational picture is the moat — not another dashboard
          of disconnected numbers.
        </p>
        <div className="mt-8 space-y-4">
          <GrayBox title="Endless relational context">
            The types of links we can draw — because everything lives in one structured layer — are what
            make downstream AI and analytics disproportionately valuable.
          </GrayBox>
          <GrayBox title="Why this supports the upside">
            The strategic value is the full stack: unified data, cross-reference, and intelligence on
            top — not a feature checklist competing with point tools.
          </GrayBox>
        </div>
      </div>
      <div className="flex items-start justify-center lg:justify-end min-w-0">
        <div className="w-full max-w-sm rounded-2xl border border-white/[0.08] bg-zinc-800/30 p-8 flex flex-col items-center gap-6">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[#FF2300]/15">
            <Brain className="h-10 w-10 text-[#FF2300]" aria-hidden />
          </div>
          <div className="flex items-center gap-2 text-zinc-500">
            <GitBranch className="h-4 w-4" aria-hidden />
            <span className="text-sm">Career graph in one place</span>
          </div>
          <Layers className="h-12 w-12 text-zinc-600" aria-hidden />
        </div>
      </div>
    </div>
  )
}
